import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  ShoppingBag,
  Heart,
  User,
  Menu,
  X,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';

const Header = () => {
  const navigate = useNavigate();
  const { totalItems, setIsCartOpen, favorites } = useCart();
  const { products, categories } = useProducts();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/magazin?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
      setSearchTerm('');
    }
  };

  const suggestions = searchTerm
    ? products
        .filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, 5)
    : [];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-100">
      {/* Announcement bar */}
      <div className="bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-2 text-center text-xs tracking-wide">
          Livrare gratuită la comenzi peste 500 lei · Retur 14 zile
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 flex items-center justify-between gap-6">
        <button
          aria-label="Meniu"
          onClick={() => setMobileOpen(true)}
          className="lg:hidden p-2 -ml-2"
        >
          <Menu className="w-6 h-6 text-neutral-900" />
        </button>

        <Link to="/" className="flex items-center gap-2.5">
          <span className="font-serif text-2xl font-bold tracking-tight text-neutral-900">AXA</span>
        </Link>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-9 text-sm font-medium text-neutral-700">
          <Link to="/magazin" className="hover:text-neutral-900 transition-colors">Magazin</Link>
          <Link to="/categorie/genti" className="hover:text-neutral-900 transition-colors">Genți</Link>
          <Link to="/categorie/ceasuri" className="hover:text-neutral-900 transition-colors">Ceasuri</Link>
          <Link to="/categorie/articole-pentru-bucatarie" className="hover:text-neutral-900 transition-colors">Bucătărie</Link>
          <Link to="/contact" className="hover:text-neutral-900 transition-colors">Contact</Link>
        </nav>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="p-2.5 hover:bg-neutral-100 rounded-full transition-colors"
            aria-label="Caută"
          >
            <Search className="w-5 h-5 text-neutral-800" />
          </button>
          <Link to="/favorite" className="relative p-2.5 hover:bg-neutral-100 rounded-full transition-colors" aria-label="Favorite">
            <Heart className="w-5 h-5 text-neutral-800" />
            {favorites.length > 0 && (
              <span className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {favorites.length}
              </span>
            )}
          </Link>
          <Link to="/cont" className="hidden sm:flex p-2.5 hover:bg-neutral-100 rounded-full transition-colors" aria-label="Cont">
            <User className="w-5 h-5 text-neutral-800" />
          </Link>
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-2.5 hover:bg-neutral-100 rounded-full transition-colors"
            aria-label="Coș"
          >
            <ShoppingBag className="w-5 h-5 text-neutral-800" />
            {totalItems > 0 && (
              <span className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="border-t border-neutral-100 bg-white">
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto px-4 lg:px-8 py-4 relative">
            <div className="flex items-center border-b-2 border-neutral-900 pb-2">
              <Search className="w-5 h-5 text-neutral-500 mr-3" />
              <input
                autoFocus
                type="text"
                placeholder="Caută produse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 outline-none text-base bg-transparent"
              />
              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchTerm('');
                }}
                className="text-neutral-400 hover:text-neutral-900 ml-3"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className="mt-3 bg-white border border-neutral-200 rounded-2xl overflow-hidden shadow-lg">
                {suggestions.map((p) => (
                  <Link
                    key={p.id}
                    to={`/produs/${p.slug}`}
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchTerm('');
                    }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors"
                  >
                    <img src={p.image} alt={p.name} className="w-12 h-12 object-cover rounded-lg" />
                    <div className="flex-1">
                      <div className="text-sm text-neutral-800 line-clamp-1">{p.name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{p.price.toFixed(2)} lei</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </form>
        </div>
      )}

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-neutral-100">
              <span className="font-serif font-bold text-2xl">AXA</span>
              <button onClick={() => setMobileOpen(false)} className="p-1">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-5">
              <Link onClick={() => setMobileOpen(false)} to="/" className="block py-3 text-lg font-medium border-b border-neutral-100">Acasă</Link>
              <Link onClick={() => setMobileOpen(false)} to="/magazin" className="block py-3 text-lg font-medium border-b border-neutral-100">Magazin</Link>
              <div className="py-4 text-xs uppercase tracking-wider text-neutral-400">Categorii</div>
              {categories.map((c) => (
                <Link
                  key={c.id}
                  onClick={() => setMobileOpen(false)}
                  to={`/categorie/${c.slug}`}
                  className="flex items-center justify-between py-2.5 text-neutral-700"
                >
                  <span>{c.name}</span>
                  <span className="text-xs text-neutral-400">({c.count})</span>
                </Link>
              ))}
              <div className="py-4 mt-2 text-xs uppercase tracking-wider text-neutral-400">Cont</div>
              <Link onClick={() => setMobileOpen(false)} to="/cont" className="block py-2.5 text-neutral-700">Conectare</Link>
              <Link onClick={() => setMobileOpen(false)} to="/favorite" className="block py-2.5 text-neutral-700">Favorite</Link>
              <Link onClick={() => setMobileOpen(false)} to="/contact" className="block py-2.5 text-neutral-700">Contact</Link>
              <Link onClick={() => setMobileOpen(false)} to="/termeni-si-conditii" className="block py-2.5 text-neutral-700">Termeni și Condiții</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
